import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  FolderOpen,
  Clock,
  Users,
  LayoutDashboard,
  Plus,
  Minus,
} from "lucide-react";
import "./ProjectTracking.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Testimonials from "../../components/Testimonials/Testimonials";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";

const TITLE = "Project Time Tracking and Team Allocation Software | TeamGrid";
const DESCRIPTION =
  "Track project progress, time distribution and team allocation in one dashboard. Identify projects that need attention before deadlines slip.";
const CANONICAL = "https://teamgrid.ai/features/project-tracking";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const TEAL = "#00D1C1";
const ICON_BG = "#E0FFFC";
const CARD_BORDER = "#EDF4FF";
const GREEN = "#34A851";
const ORANGE = "#F2673A";

const ProjectTracking = () => {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState({ 0: true });
  const toggleFaq = (i) => setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  // 2x3 grid: Row 1 — Seller7, General, EzPrep; Row 2 — E-commerce, Branding, Designing
  const projects = [
    {
      name: "Seller7",
      time: "50h 29m",
      members: "19 members",
      status: "ACTIVE",
      dot: "green",
    },
    {
      name: "General",
      time: "12h 15m",
      members: "5 members",
      status: "ACTIVE",
      dot: "green",
    },
    {
      name: "EzPrep",
      time: "4h 33m",
      members: "2 members",
      percent: "60%",
      dot: "orange",
    },
    {
      name: "E-commerce",
      time: "30h 29m",
      members: "14 members",
      status: "ACTIVE",
      dot: "green",
    },
    {
      name: "Branding",
      time: "13h 15m",
      members: "3 members",
      status: "ACTIVE",
      dot: "green",
    },
    {
      name: "Designing",
      time: "6h 33m",
      members: "2 members",
      percent: "55%",
      dot: "orange",
    },
  ];

  // FAQ data specific to Project Tracking — single Q&A
  const projectTrackingFAQs = [
    {
      question: "Can TeamGrid track time against specific projects?",
      answer:
        "Yes, Project Tracking maps usage to project workflows, helping teams see exactly where time is spent and how project budgets are tracking.",
    },
  ];

  return (
    <div className="project-tracking-container">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={CANONICAL} />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <div className="relative min-h-screen overflow-hidden bg-white">
        <PublicHeader />
        <div className="pt-16 sm:pt-18 md:pt-20">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            {/* Hero: full-bleed gradient; content aligned with header */}
            <div
              className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen pt-16 sm:pt-18 md:pt-20 pb-10 sm:pb-12 md:pb-14 px-4 sm:px-6 lg:px-8"
              style={{
                background:
                  "radial-gradient(141.42% 141.42% at 0% 0%, rgba(230, 242, 255, 0.5) 0%, rgba(233, 252, 250, 0) 50%), radial-gradient(141.42% 141.42% at 100% 100%, rgba(240, 247, 255, 0.5) 0%, rgba(240, 247, 255, 0) 50%), #FFFFFF",
              }}
            >
              <div className="max-w-7xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="flex flex-col items-center text-center"
                  style={{ gap: 20 }}
                >
                  <div
                    className="inline-flex items-center gap-2 rounded-full border px-3 py-1"
                    style={{
                      background: "rgba(0, 209, 193, 0.06)",
                      borderColor: "rgba(0, 209, 193, 0.15)",
                    }}
                  >
                    <FolderOpen
                      className="h-3 w-3 flex-shrink-0"
                      style={{ color: TEAL }}
                    />
                    <span
                      className="font-bold uppercase tracking-wide"
                      style={{
                        fontSize: 12,
                        lineHeight: "16px",
                        letterSpacing: "0.6px",
                        color: TEAL,
                      }}
                    >
                      Project Intelligence
                    </span>
                  </div>
                  <h1
                    className="font-extrabold text-black text-center"
                    style={{
                      fontSize: "clamp(2.5rem, 5vw, 62px)",
                      lineHeight: "clamp(50px, 8vw, 72px)",
                    }}
                  >
                    Project <span style={{ color: TEAL }}>Tracking</span>
                  </h1>
                  <p
                    className="mx-auto max-w-[768px] text-center"
                    style={{
                      fontSize: 20,
                      lineHeight: "28px",
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    Monitor project progress and team allocations. See exactly
                    how time is distributed across all your projects.
                  </p>
                </motion.div>
              </div>
            </div>

            {/* Main Section — white bg, padding 80px, two columns */}
            <section
              className="flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8"
              style={{
                paddingTop: 80,
                paddingBottom: 40,
                paddingLeft: 80,
                paddingRight: 80,
                background: "#FFFFFF",
              }}
            >
              <div className="w-full max-w-[1280px] mx-auto flex flex-col lg:flex-row items-center justify-center gap-16 mb-6">
                {/* Left: Complete Project Visibility */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-1 flex-col items-start lg:max-w-[608px]"
                  style={{ gap: 20 }}
                >
                  <h2
                    className="font-bold text-black"
                    style={{ fontSize: 48, lineHeight: "53px" }}
                  >
                    Complete Project Visibility
                  </h2>
                  <p
                    style={{
                      fontSize: 17,
                      lineHeight: "24px",
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    Track time allocation, team assignments, and progress for
                    every project. Get insights into which projects are on track
                    and which need attention.
                  </p>

                  <div className="flex flex-col pt-6" style={{ gap: 32 }}>
                    <div className="flex items-start gap-6">
                      <div
                        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-4 border-white"
                        style={{
                          background: ICON_BG,
                          boxShadow: "0px 0px 0px 2px rgba(0, 209, 193, 0.5)",
                        }}
                      >
                        <LayoutDashboard
                          className="h-5 w-5"
                          style={{ color: TEAL }}
                        />
                      </div>
                      <div className="flex flex-col" style={{ gap: 7 }}>
                        <h3
                          className="font-bold text-black"
                          style={{ fontSize: 15, lineHeight: "28px" }}
                        >
                          Project Dashboard
                        </h3>
                        <p
                          style={{
                            fontSize: 15,
                            lineHeight: "23px",
                            color: "rgba(0,0,0,0.6)",
                          }}
                        >
                          Overview of all projects with time and team metrics.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-6">
                      <div
                        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-4 border-white"
                        style={{
                          background: ICON_BG,
                          boxShadow: "0px 0px 0px 2px rgba(0, 209, 193, 0.5)",
                        }}
                      >
                        <Clock className="h-5 w-5" style={{ color: TEAL }} />
                      </div>
                      <div className="flex flex-col" style={{ gap: 7 }}>
                        <h3
                          className="font-bold text-black"
                          style={{ fontSize: 15, lineHeight: "28px" }}
                        >
                          Time Distribution
                        </h3>
                        <p
                          style={{
                            fontSize: 15,
                            lineHeight: "23px",
                            color: "rgba(0,0,0,0.6)",
                          }}
                        >
                          See how time is allocated across projects.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-6">
                      <div
                        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-4 border-white"
                        style={{
                          background: ICON_BG,
                          boxShadow: "0px 0px 0px 2px rgba(0, 209, 193, 0.5)",
                        }}
                      >
                        <Users className="h-5 w-5" style={{ color: TEAL }} />
                      </div>
                      <div className="flex flex-col" style={{ gap: 7 }}>
                        <h3
                          className="font-bold text-black"
                          style={{ fontSize: 15, lineHeight: "28px" }}
                        >
                          Team Allocation
                        </h3>
                        <p
                          style={{
                            fontSize: 15,
                            lineHeight: "23px",
                            color: "rgba(0,0,0,0.6)",
                          }}
                        >
                          Track which team members are working on what.
                        </p>
                      </div>
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate("/book-demo")}
                    className="rounded-xl font-bold text-black"
                    style={{
                      padding: "12px 32px",
                      background: TEAL,
                      boxShadow: "0px 0px 15px rgba(0, 209, 193, 0.3)",
                      fontSize: 16,
                      lineHeight: "24px",
                    }}
                  >
                    Explore Projects
                  </motion.button>
                </motion.div>

                {/* Right: All Projects card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="flex flex-1 flex-col w-full lg:max-w-[638px]"
                  style={{
                    background: "#FFFFFF",
                    border: `1px solid ${CARD_BORDER}`,
                    boxShadow: "0px 20px 40px -5px rgba(15, 23, 42, 0.1)",
                    borderRadius: 24,
                    overflow: "hidden",
                  }}
                >
                  <div
                    className="flex flex-col p-6 rounded-t-2xl"
                    style={{ background: "rgba(0,0,0,0.02)", gap: 4 }}
                  >
                    <div className="flex items-center gap-2">
                      <FolderOpen
                        className="h-5 w-5 flex-shrink-0"
                        style={{ color: TEAL }}
                      />
                      <h3
                        className="font-bold text-black"
                        style={{ fontSize: 20, lineHeight: "28px" }}
                      >
                        All Projects
                      </h3>
                    </div>
                    <p
                      style={{
                        fontSize: 12,
                        lineHeight: "16px",
                        fontWeight: 500,
                        color: "rgba(0,0,0,0.6)",
                      }}
                    >
                      24 Projects • 24 Active
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-4 p-6">
                    {projects.map((project, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.35 + idx * 0.05 }}
                        className="rounded-2xl border p-4 flex flex-col"
                        style={{
                          background: "rgba(0,0,0,0.02)",
                          borderColor: CARD_BORDER,
                          minHeight: 113,
                        }}
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{
                              background:
                                project.dot === "green" ? GREEN : ORANGE,
                            }}
                          />
                          <span
                            className="font-bold text-black truncate"
                            style={{ fontSize: 16, lineHeight: "24px" }}
                          >
                            {project.name}
                          </span>
                        </div>
                        <div className="flex flex-col gap-0.5 mt-auto">
                          <span
                            className="font-bold"
                            style={{
                              fontSize: 12,
                              lineHeight: "16px",
                              color: "rgba(0,0,0,0.6)",
                            }}
                          >
                            {project.time}
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              lineHeight: "16px",
                              color: "#A0A0A0",
                            }}
                          >
                            {project.members}
                          </span>
                        </div>
                        <div className="mt-2 flex justify-end">
                          {project.status ? (
                            <span
                              className="px-2 py-1 rounded bg-white font-bold uppercase"
                              style={{
                                fontSize: 10,
                                lineHeight: "15px",
                                color: GREEN,
                              }}
                            >
                              {project.status}
                            </span>
                          ) : (
                            <span
                              className="px-2 py-1 rounded bg-white font-bold uppercase"
                              style={{
                                fontSize: 10,
                                lineHeight: "15px",
                                color: ORANGE,
                              }}
                            >
                              {project.percent}
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </section>

            {/* Testimonials Section */}
            <Testimonials />

            {/* FAQ Section - Light Grey Background (full-bleed left/right) */}
            <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen py-16 px-4 sm:px-6 md:px-8 bg-[#F3F4F6]">
              <div className="max-w-4xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center mb-12"
                >
                  <span className="inline-block px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wide text-[#0D9488] bg-[#CCFBF1] border border-[#99F6E4] mb-6">
                    GOT QUESTIONS?
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    Frequently Asked Questions
                  </h2>
                  <p className="text-lg text-gray-600">
                    Everything you need to know about TeamGrid for project
                    tracking.
                  </p>
                </motion.div>

                <div className="space-y-4 w-full max-w-[720px] mx-auto">
                  {projectTrackingFAQs.map((faq, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.1 * index }}
                      className="w-full rounded-xl p-6 cursor-pointer bg-white"
                      style={{
                        border: faqOpen[index]
                          ? "1px solid #28B4AE"
                          : "1px solid #D1D5DB",
                        transition: "border-color 0.15s ease-in-out",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h3
                          className="text-left font-bold text-[#1A1A1A] flex-1"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: "26px",
                          }}
                        >
                          {faq.question}
                        </h3>
                        <span className="flex-shrink-0">
                          {faqOpen[index] ? (
                            <Minus className="w-5 h-5 text-[#111111]" />
                          ) : (
                            <Plus className="w-5 h-5 text-[#111111]" />
                          )}
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {faqOpen[index] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <p
                              className="mt-4"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: "26px",
                                color: "#374151",
                              }}
                            >
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            <ReadyToTransform />

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectTracking;
